import styles from './style.module.scss';
import { UITypography } from 'shared/ui';
import { useTranslation } from 'react-i18next';

import { BoosterType, BoosterInfo } from 'shared/types';
import { buyBooster, activateBooster } from 'shared/api';
import { useGameStore, useGlobalStore } from 'shared/store';
import classNames from 'classnames';
import { useState, useEffect } from 'react';
import { Nullable } from 'shared/types/utils';
import { BuyBoosterModal } from 'components/modals/buy-booster-modal/buy-booster-modal';
import { BOOSTERS_ICONS, BOOSTER_BUTTONS } from 'shared/constants';
import { DateTime } from 'luxon';
import { useCheckBobeBalance } from 'shared/hooks/use-check-bobe-balance';
import { useAlert } from 'shared/ui/components/alert/alert';

const BOOSTERS_LIST = [
  BoosterType.INSECTS_X,
  BoosterType.DON_X,
  BoosterType.DURATION_X,
  BoosterType.DAILY_BONUS_LIMIT_PLUS,
  BoosterType.SKIP_WAITING,
];

const BoosterCard = ({
  disabled,
  type,
  boosterInfo,
  onClick,
  onClickBuy,
  onTimerEnd,
}: {
  disabled: boolean;
  type: BoosterType;
  boosterInfo?: BoosterInfo;
  onClick: () => void;
  onClickBuy: () => void;
  onTimerEnd?: (type: BoosterType) => void;
}) => {
  const { t } = useTranslation();

  const [timeLeft, setTimeLeft] = useState(boosterInfo?.seconds_left || 0);
  const [formattedTime, setFormattedTime] = useState('');

  // const boosters = useGlobalStore((state) => state.user.boosters);
  // boosterInfo = boosters[type];

  const handleClickBuy = (event: any) => {
    // console.log('handleClickBuy');
    onClickBuy();
    event.stopPropagation();
  };

  const icon = BOOSTERS_ICONS[type];
  const title = t(`boosters.${type}.title`) || '';

  // const timer =
  //   boosterInfo && boosterInfo.seconds_left
  //     ? DateTime.fromSeconds(boosterInfo.seconds_left).toFormat('HH:mm:ss')
  //     : '';

  useEffect(() => {
    // Set initial time
    if (boosterInfo?.seconds_left) {
      setTimeLeft(boosterInfo.seconds_left);
      setFormattedTime(DateTime.fromSeconds(boosterInfo.seconds_left).toFormat('HH:mm:ss'));
    }

    // Only start timer if we have seconds_left
    if (!boosterInfo?.seconds_left) return;

    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(timer);

          setFormattedTime(''); // Скрыть таймер

          // Вызвать родительскую функцию обработки
          if (onTimerEnd) {
            onTimerEnd(type);
          }

          return 0;
        }

        const newTime = prevTime - 1;
        setFormattedTime(DateTime.fromSeconds(newTime).toFormat('HH:mm:ss'));
        return newTime;
      });
    }, 1000);

    // Clean up on unmount
    return () => clearInterval(timer);
  }, [boosterInfo?.seconds_left]);

  const boosterClassList = classNames(styles.boosterCard, {
    [styles.active]: boosterInfo?.has_active && !disabled,
    // [styles.disabled]: disabled,
  });

  return (
    <div onClick={onClick} className={boosterClassList}>
      {formattedTime && (
        <div className={styles.boosterTimerContainer}>
          <div className={styles.boosterTimer}>
            <span className={styles.boosterTimerText}>
              {formattedTime}
            </span>
          </div>
        </div>
      )}

      <img src={icon} alt={title} className={styles.boosterImage} />
      <UITypography className={styles.boosterTitle} variant="Menu" fontWeight="regular">
        {title}
      </UITypography>

      <div className={styles.buyButtonContainer}>
        <button onClick={handleClickBuy} className={styles.buyButton}>
          <img className={styles.buyButtonImage} src={BOOSTER_BUTTONS.boost} />
          {/* {boosterInfo?.count ? (
            <UITypography className={styles.buyButtonText}>{boosterInfo.count}</UITypography>
          ) : (
            null
          )} */}
          <UITypography className={styles.buyButtonText}>{boosterInfo?.count ?? 0}</UITypography>
        </button>
      </div>
    </div>
  );
};

export const Boosters = () => {
  const { checkBobeBalance, openNoBobeTokensModal } = useCheckBobeBalance();
  const { t } = useTranslation();
  const { showAlert } = useAlert();
  const boosters = useGlobalStore((state) => state.user.boosters);
  const [loading, setLoading] = useState(false);
  const [selectedBoosterType, setSelectedBoosterType] = useState<Nullable<BoosterType>>(null);
  const settings = useGameStore((state) => state.settings);
  const updateUser = useGlobalStore((state) => state.updateUser);

  const group = [BoosterType.DON_X, BoosterType.DURATION_X, BoosterType.INSECTS_X];
  const hasSomeActiveFromGroup = group.some((type) => boosters[type]?.has_active);
  const checkIsBoosterDisabled = (type: BoosterType) => {
    if (!group.includes(type)) {
      return false;
    }

    return !boosters[type]?.has_active && hasSomeActiveFromGroup;
  };

  const getBoosterAmount = (type: BoosterType) => settings[`${type}_price`];

  const handleBuyBooster = async (type: BoosterType) => {
    const amount = getBoosterAmount(type);

    if (!checkBobeBalance(amount)) {
      setSelectedBoosterType(null);
      openNoBobeTokensModal();
      return;
    }

    setLoading(true);
    buyBooster(type)
      .then((updatedUser) => {
        updateUser(updatedUser);
        setSelectedBoosterType(null);
      })
      .catch((error) => {
        showAlert(error?.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleActivateBooster = async (type: BoosterType) => {
    const booster = boosters[type];

    if (checkIsBoosterDisabled(type) || !booster || booster?.has_active) return;

    setLoading(true);
    activateBooster(type)
      .then((updatedUser) => {
        updateUser(updatedUser);
        setSelectedBoosterType(null);

        // For skip_waiting booster, reset gameReady to trigger timer recalculation
        if (type === BoosterType.SKIP_WAITING) {
          const setGameReady = useGameStore.getState().setGameReady;
          setGameReady(false);
        }
      })
      .catch((error) => {
        showAlert(error?.response?.data?.detail);
      })
      .finally(() => setLoading(false));
  };

  const handleTimerEnd = (type: BoosterType) => {
    const updatedBoosters = { ...boosters };
    if (updatedBoosters[type]) {
      updatedBoosters[type] = {
        ...updatedBoosters[type]!,
        has_active: false,
        seconds_left: 0,
      };

      // Обновить юзера с новыми данными о бустерах
      updateUser({
        ...useGlobalStore.getState().user,
        boosters: updatedBoosters,
      });
    }
  };

  return (
    <>
      <div className={styles.boostersRoot}>
        <div className={styles.overflowContainer}>
          <div className={styles.boostersContainer}>
            {BOOSTERS_LIST.map((type) => (
              <BoosterCard
                key={type}
                disabled={checkIsBoosterDisabled(type)}
                type={type}
                boosterInfo={boosters[type]}
                onClick={() => handleActivateBooster(type)}
                onClickBuy={() => setSelectedBoosterType(type)}
                onTimerEnd={handleTimerEnd}
              />
            ))}
          </div>
        </div>
      </div>

      {selectedBoosterType && (
        <BuyBoosterModal
          visible
          loading={loading}
          image={BOOSTERS_ICONS[selectedBoosterType]}
          description={t(`boosters.${selectedBoosterType}.description`) || ''}
          amount={settings[`${selectedBoosterType}_price`]}
          onClose={() => setSelectedBoosterType(null)}
          onClick={() => handleBuyBooster(selectedBoosterType)}
        />
      )}
    </>
  );
};
